import { useMemo, useState } from "react";
import NoDataFound from "../../../../../components/common/NoDataFound/NoDataFound";
import type {
  ChannelCatalogItem,
  ConnectionHubCloseReason,
  InboxGroup,
  SetupResponse,
  StandaloneCard,
} from "../../../api/connectionHub.types";
import { CONNECTION_HUB_CLOSE_REASON } from "../../../api/connectionHub.types";
import { connectionHubApi } from "../../../api/connectionHub.api";
import { isConnectionHubError } from "../../../utils/handleConnectionError";
import { type InboxStepAction, markVerifying, startsVerification } from "../../../utils/optimisticVerifying";
import { CONNECTION_STATUS } from "../../../constants/connectionStatus.constants";
import InboxCard from "../../accounts/InboxCard/InboxCard";
import DelegatedInboxCard from "../../accounts/DelegatedInboxCard/DelegatedInboxCard";
import StandaloneConnectionCard from "../../accounts/StandaloneConnectionCard/StandaloneConnectionCard";
import ConnectModalShell from "../ConnectModalShell/ConnectModalShell";
import styles from "./FinishSetupStep.module.scss";

export interface FinishSetupStepProps {
  visible: boolean;
  setup: SetupResponse;
  channels: ChannelCatalogItem[];
  agencyClientId: number | null;
  onCardUpdated: (card: InboxGroup | StandaloneCard) => void;
  onDone: () => void;
  onBack?: () => void;
  onSelectSubBrands?: () => void;
  onClose: (reason: ConnectionHubCloseReason) => void;
}

const FinishSetupStep = ({ visible, setup, channels, agencyClientId, onCardUpdated, onDone, onBack, onSelectSubBrands, onClose }: FinishSetupStepProps): JSX.Element => {
  const [busy, setBusy] = useState<{ key: string; action: InboxStepAction } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const channelsByIpId = useMemo(() => new Map(channels.map((channel) => [channel.ipId, channel])), [channels]);

  // The last confirm (filter on Gmail, the rule on Outlook) starts verification itself; "verify" is the
  // recheck's "I've Set Up Filter / Rules", which sends a failed channel straight to verification (job 14).
  const runInboxAction = async (group: InboxGroup, action: InboxStepAction, connectionIds?: string[]): Promise<void> => {
    if (busy) return;
    setBusy({ key: group.inbox.id, action });
    setErrors((current) => ({ ...current, [group.inbox.id]: "" }));
    const optimistic = startsVerification(group.inbox.provider, action);
    if (optimistic) onCardUpdated(markVerifying(group, connectionIds));
    try {
      const input = { agencyClientId, connectionIds };
      onCardUpdated(action === "forwarding"
        ? await connectionHubApi.confirmForwarding(group.inbox.id, input)
        : action === "filter"
          ? await connectionHubApi.confirmFilter(group.inbox.id, input)
          : await connectionHubApi.verifyInbox(group.inbox.id, input));
    } catch (error) {
      // Put the card back the way it was before the click, so the panel can show what went wrong.
      if (optimistic) onCardUpdated(group);
      if (isConnectionHubError(error)) setErrors((current) => ({ ...current, [group.inbox.id]: error.message }));
    } finally {
      setBusy(null);
    }
  };

  const saveAndRetry = async (group: InboxGroup, connectionId: string, password: string): Promise<boolean> => {
    if (busy) return false;
    setBusy({ key: group.inbox.id, action: "verify" });
    setErrors((current) => ({ ...current, [group.inbox.id]: "" }));
    try {
      const saved = await connectionHubApi.updateCredentials(connectionId, { password, agencyClientId });
      const updated = "inbox" in saved ? saved : group;
      onCardUpdated(markVerifying(updated, [connectionId]));
      onCardUpdated(await connectionHubApi.verifyInbox(group.inbox.id, { agencyClientId, connectionIds: [connectionId] }));
      return true;
    } catch (error) {
      if (isConnectionHubError(error)) setErrors((current) => ({ ...current, [group.inbox.id]: error.message }));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const empty = setup.inboxes.length === 0 && setup.standalone.length === 0;
  const allConnected = !empty &&
    setup.inboxes.every((group) => group.status === CONNECTION_STATUS.CONNECTED) &&
    setup.standalone.every((card) => card.status === CONNECTION_STATUS.CONNECTED);

  return (
    <ConnectModalShell
      visible={visible}
      title="Finish Setup"
      bodyClassName={styles.modalBody}
      primaryLabel="Done"
      primaryDisabled={!allConnected}
      onPrimary={onDone}
      {...(onBack ? { backLabel: "Back", onBack } : {})}
      onClose={() => onClose(CONNECTION_HUB_CLOSE_REASON.CLOSE_BUTTON)}
      onDismiss={(reason) => onClose(reason === "escape" ? CONNECTION_HUB_CLOSE_REASON.ESCAPE : CONNECTION_HUB_CLOSE_REASON.BACKDROP)}
    >
      <div className={styles.root}>
        {empty ? <NoDataFound title="No connections to finish" subTitle="Close this window and try again." /> : null}
        {setup.inboxes.length ? (
          <section className={styles.section} aria-label="Email inbox connections">
            {setup.inboxes.map((group) => group.inbox.ownerType === "delegate" ? (
              <DelegatedInboxCard key={group.inbox.id} group={group} channels={channels} agencyClientId={agencyClientId} />
            ) : (
              <InboxCard
                key={group.inbox.id}
                group={group}
                channelsByIpId={channelsByIpId}
                busyAction={busy?.key === group.inbox.id ? busy.action : null}
                actionsDisabled={Boolean(busy)}
                error={errors[group.inbox.id] || null}
                connectionErrors={errors}
                onConfirmForwarding={(item, connectionIds) => { void runInboxAction(item, "forwarding", connectionIds); }}
                onConfirmFilter={(item, connectionIds) => { void runInboxAction(item, "filter", connectionIds); }}
                onVerify={(item, connectionIds) => { void runInboxAction(item, "verify", connectionIds); }}
                onSaveAndRetry={saveAndRetry}
                onSelectSubBrands={onSelectSubBrands}
              />
            ))}
          </section>
        ) : null}
        {setup.standalone.length ? (
          <section className={styles.section} aria-label="Connections without inbox setup">
            {setup.standalone.map((card) => (
              <StandaloneConnectionCard
                key={card.connection.connectionId}
                card={card}
                channel={channelsByIpId.get(card.connection.ipId)}
                error={errors[card.connection.connectionId] || null}
                onSelectSubBrands={onSelectSubBrands}
              />
            ))}
          </section>
        ) : null}
      </div>
    </ConnectModalShell>
  );
};

export default FinishSetupStep;
